import { useThemedLogo } from "../../theme/useThemedLogo";
import { useRedmonkLogo } from "../../theme/useRedmonkLogo";
import {
  Typography,
  Button,
  Row,
  Col,
  Card,
  Space,
  Layout,
  Collapse,
} from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import PublicHeader from "../../components/PublicHeader";
import MarketingFooter from "../../components/MarketingFooter";
import Seo from "../../components/Seo";
import EmbeddedLeadForm from "../../components/EmbeddedLeadForm";
import Reveal from "../../components/Reveal";

const { Title, Paragraph } = Typography;

const PRICING_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Why is contact storage free?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We believe in empowering startups, and charging for contact storage adds unnecessary barriers. Grow your audience freely—only pay for the emails you send.",
      },
    },
    {
      "@type": "Question",
      name: "How do limits work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Free tier includes 100 emails per month. Paid plan starts at 750 INR/month for 5,000 emails. You can create a custom plan for more. If you go over in a month, you pay the overage and can upgrade your plan from next month.",
      },
    },
    {
      "@type": "Question",
      name: "What is a custom plan?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "After 5,000 emails, choose how many emails you want per month. You pay any current overage now; from next month your chosen plan amount is charged automatically.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a subscription fee?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The free tier has no fee. The paid plan is a monthly subscription starting at 750 INR for 5,000 emails, or more for a custom plan.",
      },
    },
    {
      "@type": "Question",
      name: "What is Mailinflow?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Mailinflow is a streamlined email marketing tool that simplifies bulk emailing and contact management, allowing you to focus on engaging your audience without the complexity of traditional email marketing platforms.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need to create templates to send emails?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No, Mailinflow eliminates the need for email templates. You can compose your message directly and send it to your audience effortlessly.",
      },
    },
    {
      "@type": "Question",
      name: "Can I import my existing contact lists?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, you can easily import your existing contacts or add new ones effortlessly through the contact management system.",
      },
    },
    {
      "@type": "Question",
      name: "What kind of support does Mailinflow offer?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We provide dedicated support to assist with any issues you may encounter. You can also book a demo to better understand how to use the platform.",
      },
    },
    {
      "@type": "Question",
      name: "Is Mailinflow suitable for beginners?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely! Mailinflow is designed with simplicity in mind. You don't need prior experience with email marketing to use our platform.",
      },
    },
    {
      "@type": "Question",
      name: "Can I categorize my contacts into different groups?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, you can organize your contacts into lists or groups for targeted messaging.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a free trial available?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, Mailinflow offers a free trial so you can experience our services before committing to a plan.",
      },
    },
    {
      "@type": "Question",
      name: "How quickly can I send emails?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "With Mailinflow's quick send feature, you can send emails with minimal steps—just compose, select your list, and hit send.",
      },
    },
    {
      "@type": "Question",
      name: "Are there any limits on the number of emails I can send?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The limits depend on the plan you choose — see the plan comparison for the features and limits of each plan.",
      },
    },
    {
      "@type": "Question",
      name: "What industries can benefit from Mailinflow?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Mailinflow is perfect for businesses across industries, especially those needing efficient email communication, such as marketing agencies, startups, and e-commerce companies.",
      },
    },
  ],
};

const Pricing = () => {
  const logo = useThemedLogo();
  const redmonklogo = useRedmonkLogo();
  const accessToken = localStorage.getItem("accessToken");
  const demoFormId = process.env.REACT_APP_MARKETING_DEMO_FORM_ID;

  return (
      <Layout className="marketing-page">
        <Seo
          title="Pricing — Simple, Pay-As-You-Go Forms and Email"
          description="Start free with 100 form submissions and 100 emails a month, plus unlimited contact storage. Upgrade only when you need more — no hidden fees."
          path="/pricing"
          jsonLd={PRICING_JSON_LD}
        />
        <div
          className="container"
          style={{ position: "fixed", left: 0, right: 0, zIndex: 999 }}
        >
          <PublicHeader logo={logo} accessToken={accessToken} />
        </div>

        <main>
          <div className="container">
            <section
              style={{
                padding: "9rem 0 3rem",
                textAlign: "center",
              }}
            >
              <Row gutter={30} align="center" justify="center">
                <Col sm={24} md={16}>
                  <span className="mk-eyebrow">Pay only for what you send</span>
                  <Title
                    style={{
                      margin: "1.25rem 0 1rem",
                    }}
                  >
                    Simple pricing, unlimited contacts
                  </Title>
                  <Paragraph style={{ fontSize: "16px", maxWidth: 520, margin: "0 auto 1.5rem" }}>
                    Start free with 100 form submissions and 100 emails a
                    month. Upgrade only when you need more — contact storage
                    never costs extra.
                  </Paragraph>
                  <Button
                    type="primary"
                    size="large"
                    href="https://cal.com/d/uWZdkWEAbzT3L547YuKfua/book-a-demo-mailinflow"
                    target="_blank"
                  >
                    Book a Demo
                  </Button>
                </Col>
              </Row>
            </section>

            <Reveal as="section" style={{ padding: "3rem 0" }}>
              <Card
                className="mk-card-hover"
                title={
                  <>
                    <Title level={3}>Zero to Hero</Title>
                    <Paragraph>
                      No Fees, Just Features to Get You Started.
                    </Paragraph>
                  </>
                }
                extra={<Title level={3}>0 INR/m</Title>}
                bodyStyle={{
                  paddingTop: 0,
                }}
              >
                <Space size="large" wrap>
                  <Title level={5}>
                    <CheckCircleTwoTone twoToneColor="#30EFD3" /> Unlimited
                    Contacts
                  </Title>
                  <Title level={5}>
                    <CheckCircleTwoTone twoToneColor="#30EFD3" /> 100 Form
                    Submissions
                  </Title>
                  <Title level={5}>
                    <CheckCircleTwoTone twoToneColor="#30EFD3" /> 100 Emails per
                    Month
                  </Title>
                  <Title level={5}>
                    <CheckCircleTwoTone twoToneColor="#30EFD3" /> Core Features
                  </Title>
                </Space>
                <ul>
                  <li>
                    <Paragraph>
                      <strong>No Hidden Fees</strong> - Unlimited contact
                      storage is included, always free.
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph>
                      <strong>Simple and Scalable</strong> - Upgrade when you
                      need more submissions or emails.
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph>
                      <strong>Designed for Growth</strong> - Store as many
                      contacts as you need without worrying about added costs.
                    </Paragraph>
                  </li>
                </ul>

                <Button type="primary" href="/signup" block>
                  Start Collecting and Sending for Free!
                </Button>
              </Card>
            </Reveal>

            <Reveal as="section" style={{ padding: "2rem 0" }}>
              <Title
                level={3}
                style={{ marginBottom: "0.5rem", textAlign: "center" }}
              >
                Grow past the free tier, one product at a time
              </Title>
              <Paragraph
                type="secondary"
                style={{
                  marginBottom: "1.5rem",
                  textAlign: "center",
                  maxWidth: 520,
                  margin: "0 auto 1.5rem",
                }}
              >
                Email and Forms are billed independently — scale whichever
                one you're actually using more of.
              </Paragraph>
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <Card
                    className="mk-card-hover"
                    title={<Title level={4}>Email</Title>}
                    extra={<Title level={4}>From 750 INR/m</Title>}
                  >
                    <ul>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          5,000 emails included
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          750 INR per month
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          150 INR per extra 1,000 emails (0.15 INR/email)
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Unlimited contact storage, always free
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          No templates required — compose and send directly
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Opens and clicks tracked automatically, on by
                          default
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Public and private templates — reuse your
                          best-performing emails
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Organize contacts into lists or groups for
                          targeted sends
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Submissions are spam-screened before they ever
                          become contacts
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Fast checkout with Link, cards, and more
                        </Paragraph>
                      </li>
                    </ul>
                    <Paragraph>
                      Outgrow 5,000? Scale up in a click and never pay for
                      an email you didn't send — settle today's overage now,
                      and your new plan kicks in next month.
                    </Paragraph>
                    <Button
                      type="primary"
                      href={
                        accessToken ? "/app/settings?tab=billing" : "/signup"
                      }
                      block
                    >
                      {accessToken ? "Manage in Settings" : "Get Started"}
                    </Button>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card
                    className="mk-card-hover"
                    title={<Title level={4}>Forms</Title>}
                    extra={<Title level={4}>From 1,050 INR/m</Title>}
                  >
                    <ul>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          5,000 submissions included
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          1,050 INR per month
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          210 INR per extra 1,000 submissions (~0.21
                          INR/submission)
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Unlimited forms
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Longer submission retention
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Webhooks for connecting your own tools
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Workflows to route submissions automatically
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Every submission scored for spam before it becomes
                          a contact
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Choose shadow, enforce, or off anti-spam mode per
                          form
                        </Paragraph>
                      </li>
                      <li>
                        <Paragraph style={{ marginBottom: "0.25rem" }}>
                          Embed with a single form action URL — no separate
                          form builder needed
                        </Paragraph>
                      </li>
                    </ul>
                    <Paragraph>
                      Outgrow 5,000? Scale up in a click, right from
                      Settings — no new contract, no waiting around.
                    </Paragraph>
                    <Button
                      type="primary"
                      href={
                        accessToken ? "/app/settings?tab=billing" : "/signup"
                      }
                      block
                    >
                      {accessToken ? "Manage in Settings" : "Get Started"}
                    </Button>
                  </Card>
                </Col>
              </Row>
            </Reveal>

            <Reveal
              as="section"
              style={{
                padding: "4rem 0",
              }}
            >
              <Title
                level={2}
                style={{
                  marginBottom: "2.5rem",
                  textAlign: "center",
                }}
              >
                Frequently Asked Questions
              </Title>
              <Row gutter={30} align="center">
                <Col sm={24} md={12}>
                  <Space direction="vertical" size="large">
                    <Collapse
                      collapsible="header"
                      defaultActiveKey={["1"]}
                      items={[
                        {
                          key: "1",
                          label: "Why is contact storage free?",
                          children: (
                            <p>
                              We believe in empowering startups, and charging
                              for contact storage adds unnecessary barriers.
                              Grow your audience freely—only pay for the emails
                              you send.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "2",
                          label: "How do limits work?",
                          children: (
                            <p>
                              Free tier includes 100 emails per month. Paid plan
                              starts at 750 INR/month for 5,000 emails. You can
                              create a custom plan for more (e.g. 10,000 emails
                              = 1,500 INR/month). If you go over in a month, you
                              pay the overage (150 INR per 1,000 emails) and can
                              upgrade your plan from next month.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "3",
                          label: "What is a custom plan?",
                          children: (
                            <p>
                              After 5,000 emails, choose how many emails you
                              want per month (e.g. 8k, 10k, 15k). You pay any
                              current overage now; from next month your chosen
                              plan amount is charged automatically.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "4",
                          label: "Is there a subscription fee?",
                          children: (
                            <p>
                              The free tier has no fee. The paid plan is a
                              monthly subscription (750 INR for 5,000 emails, or
                              more for a custom plan). You can pay with Link,
                              cards, and more for a fast checkout.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "5",
                          label: "What is Mailinflow?",
                          children: (
                            <p>
                              Mailinflow is a streamlined email marketing tool
                              that simplifies bulk emailing and contact
                              management, allowing you to focus on engaging your
                              audience without the complexity of traditional
                              email marketing platforms.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "6",
                          label:
                            "Do I need to create templates to send emails?",
                          children: (
                            <p>
                              No, Mailinflow eliminates the need for email
                              templates. You can compose your message directly
                              and send it to your audience effortlessly.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "7",
                          label: "Can I import my existing contact lists?",
                          children: (
                            <p>
                              Yes, you can easily import your existing contacts
                              or add new ones effortlessly through the contact
                              management system.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "8",
                          label: "What kind of support does Mailinflow offer?",
                          children: (
                            <p>
                              We provide dedicated support to assist with any
                              issues you may encounter. You can also book a demo
                              to better understand how to use the platform.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "9",
                          label: "Is Mailinflow suitable for beginners?",
                          children: (
                            <p>
                              Absolutely! Mailinflow is designed with simplicity
                              in mind. You don't need prior experience with
                              email marketing to use our platform.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "10",
                          label:
                            "Can I categorize my contacts into different groups?",
                          children: (
                            <p>
                              Yes, you can organize your contacts into lists or
                              groups for targeted messaging.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "11",
                          label: "Is there a free trial available?",
                          children: (
                            <p>
                              Yes, Mailinflow offers a free trial so you can
                              experience our services before committing to a
                              plan.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "12",
                          label: "How quickly can I send emails?",
                          children: (
                            <p>
                              With Mailinflow's quick send feature, you can send
                              emails with minimal steps—just compose, select
                              your list, and hit send.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "13",
                          label:
                            "Are there any limits on the number of emails I can send?",
                          children: (
                            <p>
                              The limits depend on the plan you choose — see the
                              plan comparison above for the features and limits
                              of each plan.
                            </p>
                          ),
                        },
                      ]}
                    />
                    <Collapse
                      collapsible="header"
                      items={[
                        {
                          key: "14",
                          label: "What industries can benefit from Mailinflow?",
                          children: (
                            <p>
                              Mailinflow is perfect for businesses across
                              industries, especially those needing efficient
                              email communication, such as marketing agencies,
                              startups, and e-commerce companies.
                            </p>
                          ),
                        },
                      ]}
                    />
                  </Space>
                </Col>
              </Row>
            </Reveal>

            {demoFormId && (
              <Reveal
                as="section"
                style={{ padding: "1rem 0 4rem" }}
                id="talk-to-sales"
              >
                <Row justify="center">
                  <Col xs={24} sm={16} md={10}>
                    <Space
                      direction="vertical"
                      align="center"
                      style={{ width: "100%", textAlign: "center" }}
                    >
                      <Title level={3} style={{ marginBottom: "0.5rem" }}>
                        Need a custom plan or have questions?
                      </Title>
                      <Paragraph type="secondary">
                        Tell us about your sending volume and we&apos;ll help
                        you pick the right plan.
                      </Paragraph>
                    </Space>
                    <EmbeddedLeadForm
                      formId={demoFormId}
                      submitLabel="Talk to sales"
                      successTitle="Thanks — we got it!"
                      successSubtitle="Someone from our team will reach out shortly."
                    />
                  </Col>
                </Row>
              </Reveal>
            )}
          </div>
        </main>

        <MarketingFooter logo={logo} redmonklogo={redmonklogo} />
      </Layout>
  );
};

export default Pricing;
